/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PrintConfigration, { ledgerPaid } from "src/print-configuration/PrintConfigration";
import CustomerCard from "./components/CustomerCard";
import OutstandingCard from "./components/OutstandingCard";
import RefundCard from "./components/RefundCard";
import OrderTable from "./components/OrderTable";
import type { SortKey, SortDir } from "./components/OrderTable";
import useSpecifiedLedger from "src/business-types/grocery/hooks/useSpecifiedLedger";
import { closeLoading, notifyError, notifyLoading, notifySuccess } from "src/assets/toastService";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { reactAppVersion } from "src/features/auth/axiosConfig";
import { errorHandling } from "src/shared/utils/errorHandling";
import { processCardPayment } from "src/paymentGatway/useCardPayment";
import { useUser } from "src/shared/context/UserContext";

type Props = {
  pageTitle: (data: string) => void;
};

export interface CustomerPayment {
  customerId: string;
  orderDate: string;
  orderTime: string;
  orderamount: string;
  outstandingBalance: number;
  paidDate: string;
  paidTime: string;
  payAmount: number;
  paymentMethod: string;
}

function formatMongoDate(value: any): string {
  if (!value) return "";

  const date =
    typeof value === "string"
      ? new Date(value)
      : value?.$date
        ? new Date(value.$date)
        : value instanceof Date
          ? value
          : null;

  if (!date || isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("en-GB", {
    timeZone: "UTC",
  });
}

function formatMongoTime(value: any): string {
  if (!value) return "";

  const date =
    typeof value === "string"
      ? new Date(value)
      : value?.$date
        ? new Date(value.$date)
        : value instanceof Date
          ? value
          : null;

  if (!date || isNaN(date.getTime())) return "";

  return date.toLocaleTimeString("en-GB", {
    timeZone: "UTC",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function SpecifiedLedgerDetails({ pageTitle }: Props) {
  const { ledgerId } = useParams();
  const id = ledgerId || "";
  const { userConfigration } = useUser();

  // ── Sort state (backend-driven) ──────────────────────────────────────────
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  // ────────────────────────────────────────────────────────────────────────

  const { ledgers, loading, postAndRefresh } = useSpecifiedLedger(
    id,
    1,
    10,
    false,
    sortKey ?? "orderedOn",
    sortDir
  );

  const [payPage, setPayPage] = useState<boolean>(false);
  const [paymode, setPaymode] = useState<string>("Cash");
  const [resPrice, setResPrice] = useState<string>("");
  const [printVisable, setPrintVisible] = useState<boolean>(false);
  const [previousData, setPreviousData] = useState<CustomerPayment>();

  const [selectedOrderId, setSelectedOrderId] = useState<string>("");

  const now = new Date();
  const localDate =
    now.getFullYear() +
    "-" +
    String(now.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(now.getDate()).padStart(2, "0");

  const navigate = useNavigate();

  const fullName = [
    ledgers?.customerDetails.firstName,
    ledgers?.customerDetails.middleName,
    ledgers?.customerDetails.lastName,
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    pageTitle(fullName);
  }, [ledgers]);

  const handlePaymentAmount = (amount: string) => {
    const number = Number(amount);
    if (
      !isNaN(number) &&
      number >= 0 &&
      number <= Math.abs(Number(ledgers?.totalOutstandingBalance))
    ) {
      setResPrice(amount);
    } else {
      notifyError("Invalid amount");
    }
  };

  const handleRowSelect = (_orderAmount: number, orderId: string) => {
    setSelectedOrderId(orderId);
  };

  const payments = async () => {
    const balance = Number(ledgers?.totalOutstandingBalance) || 0;
    const todayTime = now.toTimeString().split(" ")[0].slice(0, 8);

    const data: CustomerPayment = {
      customerId: ledgers?.customerId || "",
      outstandingBalance: ledgers?.totalOutstandingBalance || 0,
      orderamount: "0",
      orderDate: localDate,
      orderTime: todayTime,
      paymentMethod: paymode,
      payAmount: balance < 0 ? -Number(resPrice) : Number(resPrice),
      paidDate: localDate,
      paidTime: todayTime,
    };

    if (data.paymentMethod === "Card" && Boolean(userConfigration?.cardPayment) === true) {
      await processCardPayment({
        amount: data?.payAmount,
        description: `Ledger payment for ${ledgers?.customerDetails?.phones?.[0]?.phone || "Unknown Customer"}`,
        onSuccess: async (paymentDetails) => {
          const paysucess = {
            ...data,
            paymentDetails,
          };
          try {
            await postAndRefresh(paysucess);
            notifySuccess("Card payment processed successfully");
            closeLoading();
            clearPayment();
            setPrintVisible(true);
          } catch (error) {
            errorHandling(error);
          }

        },
      });
      return;
    }

    try {
      await postAndRefresh(data);
      setPrintVisible(true);
      clearPayment();
      setPreviousData(data);
    } catch (error) {
      errorHandling(error);
    }
  };

  const clearPayment = () => {
    setResPrice("");
    setPaymode("Cash");
    setPayPage(false);
  };

  if (loading) return <LoadingScreen />;
  if (!ledgers) return <div className="p-4">Ledger not found</div>;

  const normalizedOrderDetails =
    ledgers?.orderDetails?.map((item) => ({
      Orderdate: formatMongoDate(item.createdOn),
      Ordertime: formatMongoTime(item.orderedOn),
      orderid: item.orderId ?? undefined,
      Total_price: item.finalAmount ?? 0,
      ReceivedAmount: item.receivedAmount >= 0 ? item.receivedAmount : 0,
      discount: item.discount ?? 0,
      refundAmount: item.receivedAmount < 0 ? item.receivedAmount : 0,
      RemainingAmount: item.remainingAmount ?? 0,
      CurrentTotalRemaining: item.outstandingBalance ?? 0,
      order_status: item.status ?? "Unknown",
      billNumber: (item.billNumber ?? "").toString(),
      paymentSource: item.paymentSource ?? undefined,
      adjustAmount: item.adjustAmount ?? 0,
    })) ?? [];

  const checkPaymentArray = normalizedOrderDetails.length;
  const currentBalance = Number(ledgers?.totalOutstandingBalance || 0);
  const paidAmount = normalizedOrderDetails[0]?.ReceivedAmount || 0;
  const isAdvance = (previousData?.payAmount || 0) < 0;

  const printData: ledgerPaid = {
    Balance: Math.abs(currentBalance + paidAmount),
    Paid: previousData?.payAmount || 0,
    PreviousBalance: previousData?.outstandingBalance || 0,
    previousLabel: isAdvance ? "Previous Advance" : "Previous Balance",
    paidLabel: isAdvance ? "Used Amount" : "Received",
    balanceLabel: isAdvance ? "Remaining Advance" : "Outstanding Balance",
    billNo: Number(normalizedOrderDetails[checkPaymentArray - 1]?.billNumber),
    clientId: String(ledgers?.customerDetails.refId),
    CustomerPhon: Number(ledgers?.customerDetails.phones[0].phone),
    CustomerName: fullName,
    Time: now.toTimeString().split(" ")[0].slice(0, 5),
    Date: localDate,
    paymentMethod: paymode || "Cash",
  };

  return (
    <div className="w-full h-[94%] p-3 flex gap-3 bg-slate-100 box-border">
      {/* Main content */}
      <div className="flex-1 flex flex-col gap-3 min-w-0">
        {/* Top Cards */}
        <div className="grid grid-cols-3 gap-3">
          <CustomerCard
            name={fullName}
            phone={ledgers?.customerDetails.phones[0].phone}
            refId={ledgers?.customerDetails.refId}
          />
          <OutstandingCard
            balance={ledgers?.totalOutstandingBalance}
            payOpen={payPage}
            onTogglePay={() => setPayPage(!payPage)}
          />
          <RefundCard
            balance={ledgers?.totalOutstandingBalance}
            paymode={paymode}
            resPrice={resPrice}
            onPaymodeChange={setPaymode}
            onAmountChange={handlePaymentAmount}
            onConfirm={payments}
            onClose={() => setPayPage(false)}
          />
        </div>

        {/* Order Table — sort props wired in */}
        <OrderTable
          orders={normalizedOrderDetails}
          selectedOrderId={selectedOrderId}
          onRowSelect={handleRowSelect}
          onNavigate={(orderId) =>
            navigate(`/grocery/${reactAppVersion}/orders/${orderId}`)
          }
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
        />
      </div>

      {printVisable && (
        <PrintConfigration
          ledgerPaid={printData}
          openPrint={String(printVisable)}
          isClosed={() => setPrintVisible(false)}
        />
      )}
    </div>
  );
}

export default SpecifiedLedgerDetails;